import React, { useState, useEffect, useCallback } from 'react';
import PhotoUploadField from './PhotoUploadField.js';
import { isValidObjectId } from '../../utils/objectIdManager.js';

// 最小更换照片弹窗：复用PhotoUploadField上传后，调用updateForm只更新applicant_photo
const ChangePhotoDialog = ({ isOpen, onClose, onChanged, formService, formId }) => {
  const [photo, setPhoto] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setPhoto(null);
      setIsSaving(false);
      setError('');
    }
  }, [isOpen]);

  const handleSave = useCallback(async () => {
    if (!photo || !formService || !formId) return;
    if (!isValidObjectId(formId)) {
      setError('无法获取有效的表单ID，请刷新页面后重试');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const result = await formService.updateForm(formId, { applicant_photo: photo });
      if (!result || !result.success) {
        throw new Error(result?.error || '更新失败');
      }
      if (onChanged) onChanged(photo);
      onClose();
    } catch (e) {
      setError(e?.message || '保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  }, [photo, formService, formId, onClose, onChanged]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="text-lg font-semibold text-gray-800">更换申请人照片</div>
        </div>
        <div className="p-6">
          {/* 照片工具提示 - 必须使用官方工具 */}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
            <div className="text-sm text-amber-900">
              <p className="font-bold text-amber-800 mb-3">照片必须经过美国国务院官方工具检查</p>
              <p className="mb-3">请按以下步骤操作：</p>
              <ol className="list-decimal list-inside space-y-2 mb-3 text-amber-800">
                <li>点击下方按钮进入官方照片工具页面</li>
                <li>点击 <span className="font-semibold bg-amber-200 px-1 rounded">"Choose Photo"</span> 选择您的照片</li>
                <li>点击 <span className="font-semibold bg-amber-200 px-1 rounded">"Accept & Proceed"</span></li>
                <li>点击 <span className="font-semibold bg-amber-200 px-1 rounded">"Download image to your device"</span> 下载符合要求的照片</li>
                <li>将下载的照片上传到下方<span className="font-semibold">"上传新照片"</span>区域（点击选择或拖拽）</li>
              </ol>
              <a
                href="https://tsg.phototool.state.gov/photo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                进入官方照片工具
              </a>
            </div>
          </div>

          <PhotoUploadField
            label="上传新照片"
            value={photo}
            onChange={setPhoto}
            required={true}
            maxSize={3 * 1024 * 1024}
            acceptedFormats={['image/jpeg', 'image/jpg', 'image/png']}
            minDimensions={{ width: 300, height: 300 }}
            recommendedDimensions={{ width: 600, height: 600 }}
          />

          {error && (
            <div className="mt-2 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={!photo || isSaving || !isValidObjectId(formId)}
              className={`px-4 py-2 rounded-lg text-white ${(!photo || isSaving || !isValidObjectId(formId)) ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {isSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePhotoDialog;
